import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Alert,
  Chip,
  Divider,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import AttendanceReport2 from "./AttendanceReport2";
import { getApiEndpoints } from "../../../../api/config";

const AttendanceConfig = () => {
  const { t } = useTranslation();
  const { user: userStorage } = useAuth();
  const token = userStorage?.token;

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Device configuration state
  const [deviceConfig, setDeviceConfig] = useState({
    ip: "103.213.107.222",
    port: 4370,
    timeout: 30,
    password: 0,
    // Attendance timing rules
    timingRules: {
      presentTime: "09:00", // Time to be considered present
      lateTime: "09:15", // Time after which is considered late
      checkoutTime: "16:30", // Normal checkout time
      earlyLeaveTime: "16:00", // Time before which is early leave
      workingHours: 8, // Expected working hours per day
    },
  });

  // UI state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saved, setSaved] = useState(false);

  // Load saved configuration from localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem("attendance_device_config");
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        setDeviceConfig(config);
      } catch (error) {
        console.error("Error loading saved device config:", error);
      }
    }
  }, []);

  // Save configuration to localStorage
  const saveConfig = () => {
    localStorage.setItem(
      "attendance_device_config",
      JSON.stringify(deviceConfig)
    );
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  // Test device connection
  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const apiEndpoints = getApiEndpoints();
      const response = await fetch(apiEndpoints.ATTENDANCE_CONFIG_TEST, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Token ${token}` } : {}),
        },
        body: JSON.stringify(deviceConfig),
      });

      const result = await response.json();

      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        message: `Network error: ${error.message}`,
      });
    } finally {
      setTesting(false);
    }
  };

  const handleConfigChange = (field, value) => {
    const newConfig = { ...deviceConfig, [field]: value };
    setDeviceConfig(newConfig);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="attendance tabs"
          variant="fullWidth"
        >
          <Tab label="🔧 Device Configuration" />
          <Tab label="📊 Attendance Report" />
        </Tabs>
      </Paper>

      {/* Configuration Tab */}
      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🔧 Device Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure your ZK-Teco attendance device settings. Save the
              configuration before switching to the Attendance Report tab.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Device IP Address"
                  value={deviceConfig.ip}
                  onChange={(e) => handleConfigChange("ip", e.target.value)}
                  helperText="Enter the IP address of your ZK device"
                  placeholder="192.168.1.100"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Port"
                  type="number"
                  value={deviceConfig.port}
                  onChange={(e) =>
                    handleConfigChange("port", parseInt(e.target.value))
                  }
                  helperText="Default port is 4370"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Timeout (seconds)"
                  type="number"
                  value={deviceConfig.timeout}
                  onChange={(e) =>
                    handleConfigChange("timeout", parseInt(e.target.value))
                  }
                  helperText="Connection timeout in seconds"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  type="number"
                  value={deviceConfig.password}
                  onChange={(e) =>
                    handleConfigChange("password", parseInt(e.target.value))
                  }
                  helperText="Device communication password (usually 0)"
                />
              </Grid>

              <Divider sx={{ my: 3 }}>
                <Typography variant="h6" color="primary">
                  ⏰ Attendance Timing Rules
                </Typography>
              </Divider>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure the timing rules that determine attendance status.
                These rules will be used to automatically categorize attendance
                records.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Present Time"
                    type="time"
                    value={deviceConfig.timingRules?.presentTime || "09:00"}
                    onChange={(e) =>
                      setDeviceConfig({
                        ...deviceConfig,
                        timingRules: {
                          presentTime: "09:00",
                          lateTime: "09:15",
                          checkoutTime: "16:30",
                          earlyLeaveTime: "16:00",
                          workingHours: 8,
                          ...deviceConfig.timingRules,
                          presentTime: e.target.value,
                        },
                      })
                    }
                    helperText="Time to be considered present (e.g., 09:00)"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        border: "1px solid #e0e0e0",
                        "&:hover": {
                          border: "1px solid #b0b0b0",
                        },
                        "&.Mui-focused": {
                          border: "1px solid #1976d2",
                        },
                      },
                      marginLeft: "20px",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Late Time"
                    type="time"
                    value={deviceConfig.timingRules?.lateTime || "09:15"}
                    onChange={(e) =>
                      setDeviceConfig({
                        ...deviceConfig,
                        timingRules: {
                          presentTime: "09:00",
                          lateTime: "09:15",
                          checkoutTime: "16:30",
                          earlyLeaveTime: "16:00",
                          workingHours: 8,
                          ...deviceConfig.timingRules,
                          lateTime: e.target.value,
                        },
                      })
                    }
                    helperText="Time after which is considered late (e.g., 09:15)"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        border: "1px solid #e0e0e0",
                        "&:hover": {
                          border: "1px solid #b0b0b0",
                        },
                        "&.Mui-focused": {
                          border: "1px solid #1976d2",
                        },
                        marginLeft: "10px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Checkout Time"
                    type="time"
                    value={deviceConfig.timingRules?.checkoutTime || "16:30"}
                    onChange={(e) =>
                      setDeviceConfig({
                        ...deviceConfig,
                        timingRules: {
                          presentTime: "09:00",
                          lateTime: "09:15",
                          checkoutTime: "16:30",
                          earlyLeaveTime: "16:00",
                          workingHours: 8,
                          ...deviceConfig.timingRules,
                          checkoutTime: e.target.value,
                        },
                      })
                    }
                    helperText="Normal checkout time (e.g., 16:30)"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        border: "1px solid #e0e0e0",
                        "&:hover": {
                          border: "1px solid #b0b0b0",
                        },
                        "&.Mui-focused": {
                          border: "1px solid #1976d2",
                        },
                      },
                      marginLeft: "20px",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Early Leave Time"
                    type="time"
                    value={deviceConfig.timingRules?.earlyLeaveTime || "16:00"}
                    onChange={(e) =>
                      setDeviceConfig({
                        ...deviceConfig,
                        timingRules: {
                          presentTime: "09:00",
                          lateTime: "09:15",
                          checkoutTime: "16:30",
                          earlyLeaveTime: "16:00",
                          workingHours: 8,
                          ...deviceConfig.timingRules,
                          earlyLeaveTime: e.target.value,
                        },
                      })
                    }
                    helperText="Time before which is early leave (e.g., 16:00)"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        border: "1px solid #e0e0e0",
                        "&:hover": {
                          border: "1px solid #b0b0b0",
                        },
                        "&.Mui-focused": {
                          border: "1px solid #1976d2",
                        },
                      },
                      marginLeft: "10px",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Working Hours"
                    type="number"
                    value={deviceConfig.timingRules?.workingHours || 8}
                    onChange={(e) =>
                      setDeviceConfig({
                        ...deviceConfig,
                        timingRules: {
                          presentTime: "09:00",
                          lateTime: "09:15",
                          checkoutTime: "16:30",
                          earlyLeaveTime: "16:00",
                          workingHours: 8,
                          ...deviceConfig.timingRules,
                          workingHours: parseInt(e.target.value),
                        },
                      })
                    }
                    helperText="Expected working hours per day"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        border: "1px solid #e0e0e0",
                        "&:hover": {
                          border: "1px solid #b0b0b0",
                        },
                        "&.Mui-focused": {
                          border: "1px solid #1976d2",
                        },
                      },
                      marginLeft: "20px",
                    }}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    onClick={testConnection}
                    disabled={testing}
                    startIcon={testing ? <CircularProgress size={20} /> : null}
                  >
                    {testing ? "Testing..." : "Test Connection"}
                  </Button>

                  <Button
                    variant="contained"
                    onClick={saveConfig}
                    color="primary"
                  >
                    Save Configuration
                  </Button>

                  {saved && (
                    <Chip
                      label="Configuration Saved!"
                      color="success"
                      size="small"
                    />
                  )}
                </Box>
              </Grid>

              {testResult && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Alert
                    severity={testResult.success ? "success" : "error"}
                    sx={{ mt: 2 }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Connection Test Result:
                    </Typography>
                    <Typography variant="body2">
                      {testResult.message}
                    </Typography>
                    {testResult.device_ip && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 1 }}
                      >
                        Device: {testResult.device_ip}:{testResult.device_port}
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              )}
            </Grid>

            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                💡 Configuration Tips:
              </Typography>
              <Typography variant="body2" component="div">
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>
                    Make sure the device is powered on and connected to the
                    network
                  </li>
                  <li>
                    Check that the IP address is correct in your device settings
                  </li>
                  <li>Ensure port 4370 is not blocked by firewall</li>
                  <li>
                    Try different passwords (0, empty, or device default) if
                    connection fails
                  </li>
                  <li>
                    Verify the device is in "Ethernet" mode, not standalone
                  </li>
                  <li>
                    <strong>
                      Save configuration before switching to Attendance Report
                    </strong>
                  </li>
                </ul>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Attendance Report Tab */}
      {tabValue === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              📊 Attendance Report
            </Typography>
            <Typography variant="body2">
              Using device configuration: {deviceConfig.ip}:{deviceConfig.port}
            </Typography>
            <Typography variant="body2">
              If you need to change device settings, switch back to the
              Configuration tab.
            </Typography>
          </Alert>
          <AttendanceReport2 deviceConfig={deviceConfig} />
        </Box>
      )}
    </Box>
  );
};

export default AttendanceConfig;
